import { useCallback, useEffect, useRef } from 'react';
import { LabeledSection } from './LabeledSection';
import { i18n } from 'app/utils/i18n';
import { useUpdateTag } from 'app/api/tag-queries';

interface NotesSectionProps {
  tagId: string;
  documentSuperId: number;
  defaultValue: string;
  delay?: number;
  disabled?: boolean;
}

export const NotesSection = ({ tagId, documentSuperId, defaultValue, delay = 500, disabled = false }: NotesSectionProps) => {
  const { mutate: updateTag } = useUpdateTag();
  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    if (pendingRef.current !== null) {
      updateTag({ id: tagId, notes: pendingRef.current, documentSuperId });
      pendingRef.current = null;
    }
  }, [tagId, documentSuperId, updateTag]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      pendingRef.current = e.target.value;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, delay);
    },
    [flush, delay]
  );

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      flush();
    };
  }, [flush]);

  return (
    <LabeledSection label={i18n('tag.notes.title')}>
      <textarea
        className="notes-textarea"
        placeholder={i18n('tag.notes.add')}
        defaultValue={defaultValue}
        onChange={handleChange}
        rows={3}
        disabled={disabled}
      />
    </LabeledSection>
  );
};
